/**
 * Authentication Context
 * Manages user authentication state with Firebase
 *
 * Features:
 * - Firebase authentication with automatic token refresh
 * - Inactivity logout (4h prod, 8h dev)
 * - Session persistence across browser restarts
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, type User, type LoginCredentials } from '@/services/api/authService';
import { firebaseAuthService } from '@/services/firebase/firebaseAuthService';
import { tokenManager } from '@/lib/api-client';
import { TOKEN_KEYS } from '@/config/api.config';
import { clearHolidayCache } from '@/utils/holidays';
import { useInactivityLogout } from '@/hooks/useInactivityLogout';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Automatic logout after inactivity (4h prod, 8h dev)
  useInactivityLogout({
    enabled: !!user, // Only track when user is authenticated
  });

  // Check if user is authenticated on mount
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    // Safety timeout: if auth initialization takes longer than 10 seconds,
    // force isLoading to false so the app doesn't stay stuck on "Cargando..."
    // This handles cases where Firebase SDK hangs, network is unreachable,
    // or onAuthStateChange never fires.
    const safetyTimeout = setTimeout(() => {
      setIsLoading((current) => {
        if (current) {
          console.warn('[AUTH] Safety timeout: auth initialization took >10s, unblocking app');
        }
        return false;
      });
    }, 10_000);

    const initializeAuth = async () => {
      // Skip all auth initialization when running inside an iframe
      // (e.g., heatmap preview). The public quote page works without auth,
      // and running Firebase in an iframe can clear the parent's auth tokens.
      if (window.self !== window.top) {
        clearTimeout(safetyTimeout);
        setIsLoading(false);
        return;
      }

      try {
        // Check if Firebase is configured
        if (firebaseAuthService.isConfigured()) {
          // Set up Firebase auth state listener for persistence
          unsubscribe = firebaseAuthService.onAuthStateChange(async (firebaseUser) => {
            if (firebaseUser) {
              try {
                // Get fresh token
                const firebaseToken = await firebaseUser.getIdToken();
                localStorage.setItem(TOKEN_KEYS.FIREBASE, firebaseToken);

                // Always get fresh user profile from backend
                try {
                  const userProfile = await authService.getProfile();
                  setUser(userProfile);
                  localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(userProfile));
                } catch (err) {
                  // Try stored user as fallback
                  const storedUser = localStorage.getItem(TOKEN_KEYS.USER);
                  if (storedUser) {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                  } else {
                    setUser(null);
                  }
                } finally {
                  setIsLoading(false);
                }
              } catch (error) {
                console.error('❌ [AUTH] Error getting user profile:', error);
                setUser(null);
                setIsLoading(false);
              }
            } else {
              // No Firebase user
              setUser(null);
              setIsLoading(false);
            }
          });
        } else {
          // Fallback to JWT for backward compatibility
          const accessToken = tokenManager.getAccessToken();

          if (accessToken) {
            const userProfile = await authService.getProfile();
            setUser(userProfile);
          } else {
            const storedUser = tokenManager.getUser();
            if (storedUser) {
              setUser(storedUser);
            }
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('❌ [AUTH] Error initializing auth:', error);
        // Clear invalid tokens - but not in iframes to avoid logging out parent window
        if (window.self === window.top) {
          tokenManager.clearTokens();
        }
        setUser(null);
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Cleanup listener and safety timeout on unmount
    return () => {
      clearTimeout(safetyTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use Firebase authentication
      const { user: firebaseUser } = await firebaseAuthService.signIn(
        credentials.email,
        credentials.password
      );

      setUser(firebaseUser);
    } catch (error: any) {
      // Traducir errores de Firebase a mensajes amigables
      const rawMessage = error.message || '';
      let errorMessage: string;

      if (rawMessage.includes('invalid-credential') || rawMessage.includes('wrong-password') || rawMessage.includes('user-not-found')) {
        errorMessage = 'Correo electrónico o contraseña incorrectos';
      } else if (rawMessage.includes('too-many-requests')) {
        errorMessage = 'Demasiados intentos fallidos. Intenta de nuevo más tarde';
      } else if (rawMessage.includes('user-disabled')) {
        errorMessage = 'Esta cuenta ha sido deshabilitada';
      } else if (rawMessage.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu internet';
      } else {
        errorMessage = 'Error al iniciar sesión. Intenta de nuevo';
      }

      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Logout from Firebase
      await firebaseAuthService.signOut();

      // Clear cached holidays
      clearHolidayCache();

      // Clear local state
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear cached holidays even on error
      clearHolidayCache();
      // Clear state anyway
      setUser(null);
      setError(null);
    }
  };

  const updateProfile = async (data: any) => {
    try {
      setError(null);
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update profile';
      setError(errorMessage);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userProfile = await authService.getProfile();
      setUser(userProfile);
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    updateProfile,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

